import React, { useEffect, useState } from "react";

const initialState = {
  customerName: "",
  phoneNumber: "",
  country: "",
  state: "",
  city: "",
  address: "",
  instructions: "",
};

const fieldLabels = {
  customerName: "Customer Name",
  phoneNumber: "Phone Number",
  country: "Country",
  state: "State",
  city: "City",
  address: "Delivery Address",
  instructions: "Additional Delivery Instructions (Optional)",
};

function DeliveryInfoModal({
  product,
  isOpen,
  onClose,
  onSubmit,
  submitLabel = "Continue to WhatsApp",
}) {
  const [formData, setFormData] = useState(initialState);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setFormData(initialState);
      setErrors({});
      setSubmitting(false);
    }
  }, [isOpen]);

  useEffect(() => {
    const handleEsc = (event) => {
      if (event.key === "Escape" && isOpen) {
        onClose();
      }
    };

    document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validate = () => {
    const nextErrors = {};
    const requiredFields = [
      "customerName",
      "phoneNumber",
      "country",
      "state",
      "city",
      "address",
    ];

    requiredFields.forEach((field) => {
      const value = String(formData[field] || "").trim();
      if (!value) {
        nextErrors[field] = `${fieldLabels[field]} is required.`;
      }
    });

    if (formData.phoneNumber && formData.phoneNumber.trim().length < 7) {
      nextErrors.phoneNumber = "Please enter a valid phone number.";
    }

    return nextErrors;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const nextErrors = validate();

    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      return;
    }

    setSubmitting(true);
    try {
      const sanitized = {
        customerName: formData.customerName.trim(),
        phoneNumber: formData.phoneNumber.trim(),
        country: formData.country.trim(),
        state: formData.state.trim(),
        city: formData.city.trim(),
        address: formData.address.trim(),
        instructions: (formData.instructions || "").trim(),
      };

      await onSubmit(sanitized);
      setFormData(initialState);
      setErrors({});
      onClose();
    } catch (error) {
      console.error(error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="delivery-modal-overlay"
      aria-modal="true"
      role="dialog"
      onClick={onClose}
    >
      <div
        className="delivery-modal"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="delivery-modal-header">
          <div>
            <h3>Delivery Information</h3>
            <p>
              Ordering: <strong>{product?.name || "Selected product"}</strong>
            </p>
          </div>
          <button
            type="button"
            className="delivery-modal-close"
            onClick={onClose}
            aria-label="Close delivery form"
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="delivery-form" noValidate>
          <div className="delivery-form-grid">
            <div className="delivery-field">
              <label htmlFor="customerName">{fieldLabels.customerName}</label>
              <input
                id="customerName"
                name="customerName"
                value={formData.customerName}
                onChange={handleChange}
                placeholder="Enter your full name"
              />
              {errors.customerName && (
                <small className="form-error">{errors.customerName}</small>
              )}
            </div>

            <div className="delivery-field">
              <label htmlFor="phoneNumber">{fieldLabels.phoneNumber}</label>
              <input
                id="phoneNumber"
                name="phoneNumber"
                value={formData.phoneNumber}
                onChange={handleChange}
                placeholder="e.g. +234..."
              />
              {errors.phoneNumber && (
                <small className="form-error">{errors.phoneNumber}</small>
              )}
            </div>

            <div className="delivery-field">
              <label htmlFor="country">{fieldLabels.country}</label>
              <input
                id="country"
                name="country"
                value={formData.country}
                onChange={handleChange}
                placeholder="Country"
              />
              {errors.country && (
                <small className="form-error">{errors.country}</small>
              )}
            </div>

            <div className="delivery-field">
              <label htmlFor="state">{fieldLabels.state}</label>
              <input
                id="state"
                name="state"
                value={formData.state}
                onChange={handleChange}
                placeholder="State"
              />
              {errors.state && (
                <small className="form-error">{errors.state}</small>
              )}
            </div>

            <div className="delivery-field">
              <label htmlFor="city">{fieldLabels.city}</label>
              <input
                id="city"
                name="city"
                value={formData.city}
                onChange={handleChange}
                placeholder="City"
              />
              {errors.city && (
                <small className="form-error">{errors.city}</small>
              )}
            </div>
          </div>

          <div className="delivery-field">
            <label htmlFor="address">{fieldLabels.address}</label>
            <textarea
              id="address"
              name="address"
              value={formData.address}
              onChange={handleChange}
              placeholder="Enter your delivery address"
              rows="3"
            />
            {errors.address && (
              <small className="form-error">{errors.address}</small>
            )}
          </div>

          <div className="delivery-field">
            <label htmlFor="instructions">{fieldLabels.instructions}</label>
            <textarea
              id="instructions"
              name="instructions"
              value={formData.instructions}
              onChange={handleChange}
              placeholder="Optional instructions for the delivery"
              rows="3"
            />
          </div>

          <div className="delivery-modal-actions">
            <button type="button" className="delivery-cancel" onClick={onClose}>
              Cancel
            </button>
            <button
              type="submit"
              className="delivery-submit"
              disabled={submitting}
            >
              {submitting ? "Processing..." : submitLabel}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default DeliveryInfoModal;
